import React, { useEffect, useRef, useState } from 'react'
import DayPickerInput from 'react-day-picker/DayPickerInput'
import { DateUtils, DayModifiers, DayPickerInputProps } from 'react-day-picker'
import dateFnsFormat from 'date-fns/format'
import dateFnsParse from 'date-fns/parse'

import 'react-day-picker/lib/style.css'

import { CalenderIcon } from 'src/common'
import { INTERACTIONS, DIRECTIONS } from 'src/helpers'

import { Button, IButtonProps } from '../button'
import { IInputIconProps, InputIcon } from '../input'

function parseDate(str: string, format: string, locale: string) {
  const parsed = dateFnsParse(str, format, new Date(), { locale: (locale as unknown) as Locale })
  if (DateUtils.isDate(parsed)) {
    return parsed
  }
  return undefined
}

function formatDate(date: Date, format: string, locale: string) {
  return dateFnsFormat(date, format, { locale: (locale as unknown) as Locale })
}

const FORMAT = 'MMMM d, y'

export type IDayPicker = DayPickerInputProps & {
  defaultValue?: string | Date
}

type IButtonIconProps = IButtonProps & {
  icon: React.ReactNode
}

export const DayPickerButton: React.FC<IDayPicker> = (props: IDayPicker) => {
  return (
    <div className="w-100 day-picker-wrapper">
      <DayPickerInput
        component={(props: IButtonIconProps) => {
          return (
            <Button className="button button-icon button-day_picker w-100" {...props}>
              <span className="mr-12px ml-5px w-28px h-28px">
                <CalenderIcon className="w-100 h-100" />
              </span>
              {props.value || props.placeholder}
            </Button>
          )
        }}
        format={FORMAT}
        formatDate={formatDate}
        parseDate={parseDate}
        placeholder="Select Date"
        {...props}
      />
    </div>
  )
}

const DayInput = (props: IInputIconProps) => {
  return <InputIcon className="basic" type="text" {...props} />
}

export const DayPickerText: React.FC<IDayPicker> = (props: IDayPicker) => {
  const { inputProps, defaultValue, value, onDayChange, format, ...otherProps } = props
  const inputInstance = useRef<DayPickerInput>(null)
  const [internalValue, setInternalValue] = useState(defaultValue || value)

  const handleDayChange = (day: Date, dayModifiers: DayModifiers, dayPickerInput: DayPickerInput) => {
    onDayChange?.(day, dayModifiers, dayPickerInput)
  }

  useEffect(() => {
    if (value && !defaultValue) setInternalValue(value)
  }, [defaultValue, value])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const { key, shiftKey } = e

    if (!shiftKey && (key === INTERACTIONS.TAB || key === DIRECTIONS.DOWN)) {
      setTimeout(() => {
        inputInstance.current?.showDayPicker()
        setTimeout(() => {
          const dayPickerWrapper = document.getElementsByClassName('DayPicker-wrapper')[0] as HTMLElement
          return dayPickerWrapper ? dayPickerWrapper.focus() : ''
        }, 40)
      }, 10)
    }
  }

  const handleHiddenPicker = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const { key, shiftKey } = event

    const checkExist =
      document.getElementsByClassName('DayPicker-Body')[0] &&
      document.getElementsByClassName('DayPicker-Body')[0].contains(event.target as Node)

    if (!shiftKey && key === INTERACTIONS.TAB && checkExist) {
      inputInstance.current?.hideDayPicker()
    }
  }

  return (
    <div className="w-100 day-picker-wrapper" onKeyDown={handleHiddenPicker}>
      <DayPickerInput
        ref={inputInstance}
        keepFocus={true}
        component={DayInput}
        format={format || FORMAT}
        formatDate={formatDate}
        parseDate={parseDate}
        placeholder="Select Date"
        inputProps={{ ...inputProps, ref: null, onKeyDown: handleKeyDown }}
        value={internalValue}
        onDayChange={handleDayChange}
        {...otherProps}
      />
    </div>
  )
}
